import React, { useState, useEffect, createContext, useContext } from "react";
import { Dropdown, Card } from "react-bootstrap";
import { FaGlobe } from "react-icons/fa";

// Create a context for language
export const LanguageContext = createContext();

// Language provider component
export const LanguageProvider = ({ children }) => {
  // Get saved language from localStorage or default to Thai
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem("ruaysure-language");
    return savedLanguage || "th";
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem("ruaysure-language", language);
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Hook to use language context
export const useLanguage = () => useContext(LanguageContext);

// Language translations
export const translations = {
  th: {
    // Common
    dashboard: "แดชบอร์ด",
    booking: "จองบริการ",
    packages: "แพ็กเกจ",
    profile: "โปรไฟล์",
    contact: "ติดต่อเรา",
    help: "ช่วยเหลือ",
    login: "เข้าสู่ระบบ",
    register: "สมัครสมาชิก",
    logout: "ออกจากระบบ",
    notifications: "การแจ้งเตือน",
    adsStatus: "สถานะโฆษณา",
    
    // Dashboard
    welcomeBack: "ยินดีต้อนรับกลับ",
    yourPage: "เพจของคุณ",
    salesRep: "พนักงานขาย",
    adsStaff: "พนักงานดูแลโฆษณา",
    activePackages: "แพ็กเกจที่ใช้งานได้",
    upcomingBookings: "การจองที่กำลังจะถึง",
    packageExpiry: "แพ็กเกจใกล้หมดอายุ",
    
    // Booking
    bookService: "จองบริการ",
    selectStaff: "เลือกพนักงาน",
    selectDateTime: "เลือกวันและเวลา",
    serviceType: "ประเภทบริการ",
    budget: "งบประมาณ",
    specialRequirements: "ความต้องการพิเศษ",
    bookingHistory: "ประวัติการจอง",
    
    // Packages
    availablePackages: "แพ็กเกจที่มีให้บริการ",
    buyNow: "ซื้อเลย",
    packageDetails: "รายละเอียดแพ็กเกจ",
    purchaseHistory: "ประวัติการซื้อ",
    
    // Profile
    personalInfo: "ข้อมูลส่วนตัว",
    facebookPage: "เพจ Facebook",
    connectFacebook: "เชื่อมต่อกับ Facebook",
    
    // Contact
    sendMessage: "ส่งข้อความ",
    messageCategory: "หมวดหมู่ข้อความ",
    priority: "ความสำคัญ",
    
    // Help Center
    userGuide: "คู่มือการใช้งาน",
    troubleshooting: "แก้ไขปัญหา",
    contactInfo: "ข้อมูลติดต่อ",
    supportRequest: "ขอความช่วยเหลือ",
    
    // Ads Status
    adsPerformance: "ประสิทธิภาพโฆษณา",
    reach: "การเข้าถึง",
    engagement: "การมีส่วนร่วม",
    clicks: "คลิก",
    ctr: "อัตราการคลิก",
    
    // Notifications
    notificationSettings: "ตั้งค่าการแจ้งเตือน",
    markAllAsRead: "ทำเครื่องหมายอ่านแล้วทั้งหมด",
    
    // Buttons
    save: "บันทึก",
    cancel: "ยกเลิก",
    submit: "ส่ง",
    confirm: "ยืนยัน",
    back: "ย้อนกลับ",
    next: "ถัดไป"
  },
  en: {
    // Common
    dashboard: "Dashboard",
    booking: "Book Service",
    packages: "Packages",
    profile: "Profile",
    contact: "Contact Us",
    help: "Help Center",
    login: "Login",
    register: "Register",
    logout: "Logout",
    notifications: "Notifications",
    adsStatus: "Ads Status",
    
    // Dashboard
    welcomeBack: "Welcome Back",
    yourPage: "Your Page",
    salesRep: "Sales Representative",
    adsStaff: "Ads Staff",
    activePackages: "Active Packages",
    upcomingBookings: "Upcoming Bookings",
    packageExpiry: "Package Expiry",
    
    // Booking
    bookService: "Book Service",
    selectStaff: "Select Staff",
    selectDateTime: "Select Date & Time",
    serviceType: "Service Type",
    budget: "Budget",
    specialRequirements: "Special Requirements",
    bookingHistory: "Booking History",
    
    // Packages
    availablePackages: "Available Packages",
    buyNow: "Buy Now",
    packageDetails: "Package Details",
    purchaseHistory: "Purchase History",
    
    // Profile
    personalInfo: "Personal Information",
    facebookPage: "Facebook Page",
    connectFacebook: "Connect with Facebook",
    
    // Contact
    sendMessage: "Send Message",
    messageCategory: "Message Category",
    priority: "Priority",
    
    // Help Center
    userGuide: "User Guide",
    troubleshooting: "Troubleshooting",
    contactInfo: "Contact Information",
    supportRequest: "Support Request",
    
    // Ads Status
    adsPerformance: "Ads Performance",
    reach: "Reach",
    engagement: "Engagement",
    clicks: "Clicks",
    ctr: "CTR",
    
    // Notifications
    notificationSettings: "Notification Settings",
    markAllAsRead: "Mark All as Read",
    
    // Buttons
    save: "Save",
    cancel: "Cancel",
    submit: "Submit",
    confirm: "Confirm",
    back: "Back",
    next: "Next"
  }
};

// Language switcher component
const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
  };

  return (
    <Dropdown>
      <Dropdown.Toggle as="a" className="search-toggle d-flex align-items-center" id="language-dropdown">
        <FaGlobe style={{ color: "#f77c1c", fontSize: "1.2rem" }} className="me-1" />
        <span className="d-none d-md-inline">{language === "th" ? "ไทย" : "English"}</span>
      </Dropdown.Toggle>

      <Dropdown.Menu className="sub-drop">
        <Card className="shadow-none m-0">
          <Card.Header>
            <div className="header-title">
              <h5 className="mb-0">Language</h5>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <Dropdown.Item 
              active={language === "th"} 
              onClick={() => handleLanguageChange("th")}
              className="d-flex align-items-center iq-sub-card border-0"
            >
              <span className="material-symbols-outlined">language</span>
              <div className="ms-3">ไทย (Thai)</div>
            </Dropdown.Item>
            <Dropdown.Item 
              active={language === "en"} 
              onClick={() => handleLanguageChange("en")}
              className="d-flex align-items-center iq-sub-card border-0"
            >
              <span className="material-symbols-outlined">language</span>
              <div className="ms-3">English</div>
            </Dropdown.Item>
          </Card.Body>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default LanguageSwitcher;
